<template>
	<DialogWindow
			:title="title"
			:close-check="canDialogBeClosedWithoutLosingUploads"
			dialog-context-id="UploadDialog"
	>
		<div class="upload-input" data-testid="popup-body">
			<!-- Empty dragOver needed to preventDefault opening file in new tab. Enter and Leave are for visual feedback. -->
			<!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
			<div
					class="dragndrop"
					@drop.prevent="fileChanged"
					@dragover.prevent=""
					@dragenter.prevent="dragEnter"
					@dragleave.prevent="dragLeave"
					@drop="dragLeave"
			>
				<div class="warning">Niet alle bestanden zijn geldig.</div>

				<div>
					Drag & drop of
					<label v-if="!topicIrrelevant" class="target">
						Kies een bestand om toe te voegen
						<input
								multiple
								type="file"
								:max="maxFiles"
								:accept="allowedMimeTypes.join(',')"
								@change="fileChanged"
						/>
					</label>
				</div>
			</div>
			<ul>
				<li v-for="attachment in currentExistingAttachments" :key="attachment.uploadId">
					<AttachmentItem
							:claimed-attachment="attachment"
							:readonly="topicIrrelevant"
							@download="emit('download', attachment.uploadId)"
							@delete="removedAttachmentIds.push(attachment.uploadId)"
					/>
				</li>

				<li v-for="upload in newAttachments" :key="upload.id">
					<AttachmentItem
							:upload="upload"
							:readonly="topicIrrelevant"
							:downloadable="upload.tag === 'uploaded' ? props.canDownload(upload.upload) : false"
							@download="upload.tag === 'uploaded' ? emit('download', upload.upload.id) : false"
							@delete="deleteUpload(upload)"
					/>
				</li>
			</ul>

			<div class="dialog-buttons">
				<BaseButton
						btn-class="secondary"
						text="Annuleren"
						@click="cancel"
				/>
				<BaseButton
						text="Toevoegen"
						:disabled="uploadingInProgress"
						@click="save"
				/>
			</div>
		</div>
	</DialogWindow>
</template>

<script setup lang="ts">
	import type {Ref} from 'vue';
	import {computed, onMounted, onUnmounted, reactive, ref} from 'vue';
	import type {ClaimedUpload, UnclaimedUpload} from '~/lib/types/uploads';
	import useApi from '~/composables/useApi';
	import DialogWindow from '~/components/generic/DialogWindow.vue';
	import BaseButton from '~/components/generic/buttons/BaseButton.vue';
	import {allowedMimeTypes, maxFiles, maxFileSizeInMb} from '~/composables/uploads/uploadConfiguration';
	import type {ProblemDetail} from '~/lib/types/fetchErrors';
	import AttachmentItem from '~/composables/uploads/AttachmentItem.vue';

	type ErrorState =
		| {
		tag: 'httpError',
		filename: string,
		status: number,
		statusText?: string,
		id: string
	}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		| { tag: 'error', error: any, filename: string, id: string }
		| { tag: 'validationError', filename: string, violationMessage?: string, id: string }

	export type UploadState =
		| { tag: 'uploading', file: File, id: string }
		| { tag: 'uploaded', file?: File, upload: UnclaimedUpload, id: string }
		| ErrorState

	const props = defineProps<{
		claimedAttachments: ClaimedUpload[],
		unclaimedAttachments: UnclaimedUpload[],
		// eslint-disable-next-line no-unused-vars
		canDownload: (upload: UnclaimedUpload) => boolean,
		topicIrrelevant?: boolean,
	}>();

	const emit = defineEmits<{
		// eslint-disable-next-line no-unused-vars
		(tag: 'uploadStarted', index: number, filename: string): void,
		// eslint-disable-next-line no-unused-vars
		(tag: 'uploadFinished', id: string, filename: string, upload: UnclaimedUpload): void,
		// eslint-disable-next-line no-unused-vars
		(tag: 'uploadRemoved', id: string): void,
		// eslint-disable-next-line no-unused-vars, @typescript-eslint/unified-signatures
		(tag: 'download', id: string): void,
		// eslint-disable-next-line no-unused-vars
		(tag: 'done'): void,
	}>();

	const api = useApi();
	const state = reactive(new Map<string, UploadState>());
	const removedAttachmentIds: Ref<string[]> = ref([]);

	const newAttachments = computed(() => {
		const existingAttachmentIds = props.claimedAttachments?.map(attachment => attachment.uploadId);

		const theseNewAttachments: UploadState[] = Array.from(state.values())
			.filter(upload => !(upload.tag === 'uploaded' && existingAttachmentIds.includes(upload.upload.id)));

		const previousNewAttachments = props.unclaimedAttachments
			.map((a): UploadState => ({id: crypto.randomUUID(), tag: 'uploaded', upload: a}))
			.filter(upload => !(upload.tag === 'uploaded' && existingAttachmentIds.includes(upload.upload.id)))
			.filter(upload => ('upload' in upload ? !removedAttachmentIds.value.includes(upload.upload.id) : true));

		return [...theseNewAttachments, ...previousNewAttachments];
	});

	const currentExistingAttachments = computed(() => {
		return props.claimedAttachments?.filter(attachment => !removedAttachmentIds.value.includes(attachment.uploadId));
	});

	const title = computed(() => {
		const currentTotalAttachments = (currentExistingAttachments?.value?.length ?? 0) +
			(newAttachments?.value?.filter(a => a.tag.toLowerCase().includes('uploaded')).length ?? 0);
		return currentTotalAttachments + (currentTotalAttachments !== 1 ? ' Bijlagen' : ' Bijlage');
	});

	const classDrag = 'drag';
	const classInvalid = 'invalid';

	const dragEnter = (event: DragEvent) => {
		event.preventDefault();
		const classes: string[] = [classDrag];

		const files = [...(event.dataTransfer?.items ?? [])];
		if (files.length + currentExistingAttachments.value.length + newAttachments.value.length > maxFiles) {
			classes.push(classInvalid);
		} else if (!files.every(file => allowedMimeTypes.includes(file.type))) {
			classes.push(classInvalid);
		}

		(event.target as HTMLElement).classList.add(...classes);
	};

	const dragLeave = (event: DragEvent) => {
		event.preventDefault();
		(event.target as HTMLElement).classList.remove(classDrag, classInvalid);
	};

	const attemptUpload = (files: File[]) => {
		files.forEach(async (file) => {
			const id = crypto.randomUUID();

			state.set(id, {tag: 'uploading', file, id});

			const formData = new FormData();
			formData.append('file', file);
			formData.append('filename', file.name);

			try {
				const response = await api.upload(formData);

				if (response.ok) {
					const data = await response.json();
					state.set(id, {tag: 'uploaded', file, upload: data, id});
				} else {
					const problemDetail: ProblemDetail = await response.json();
					state.set(id, {
						tag: 'httpError',
						filename: file.name,
						status: response.status,
						statusText: problemDetail.detail ?? response.statusText,
						id
					});
				}
			} catch (error) {
				state.set(id, {tag: 'error', error, filename: file.name, id});
			}
		});
	};

	const fileChanged = (event: Event | DragEvent) => {
		const target = ('dataTransfer' in event ? event.dataTransfer : event.target) as HTMLInputElement;
		const files = Array.from(target?.files || []);
		const totalCurrentFiles = currentExistingAttachments.value.length +
			newAttachments.value.filter(a => !a.tag.toLowerCase().includes('error')).length;

		const filesTooLarge = files
			.filter(file => file.size > (1024 * 1024 * maxFileSizeInMb));
		const filesWrongMime = files
			.filter(f => !filesTooLarge.includes(f))
			.filter(file => !allowedMimeTypes.includes(file.type));
		const filesTooMany = files
			.filter(f => ![...filesTooLarge, ...filesWrongMime].includes(f))
			.filter((_, index) => totalCurrentFiles + index >= maxFiles);
		const correctFiles = files.filter(file => ![
			...filesTooLarge,
			...filesWrongMime,
			...filesTooMany
		].includes(file));

		[...filesTooLarge, ...filesWrongMime, ...filesTooMany].forEach(file => {
			const id = crypto.randomUUID();
			const filename = file.name;
			const tag = 'validationError';
			let violationMessage: string;
			if (filesTooLarge.includes(file)) {
				violationMessage = `De maximale bestandsgrootte is ${maxFileSizeInMb}MB`;
			} else if (filesWrongMime.includes(file)) {
				violationMessage = 'Bestandstype niet ondersteund';
			} else {
				violationMessage = `Je kunt maximaal ${maxFiles} bestanden toevoegen`;
			}
			state.set(id, {tag, filename, violationMessage, id});
		});

		attemptUpload(correctFiles);
	};

	const deleteUpload = (upload: UploadState) => {
		if (upload.tag === 'uploaded') {
			state.delete(upload.id);
			removedAttachmentIds.value.push(upload.upload.id);
		}
	};

	const save = () => {
		// On save, remove deleted values from existing downloads
		Array.from(state.values())
			.filter(attachment => attachment.tag === 'uploaded')
			.forEach((attachment) => {
				const data = attachment as { tag: 'uploaded', file?: File, upload: UnclaimedUpload, id: string };
				emit('uploadFinished', data.id, data.upload.filename, data.upload);
			});
		removedAttachmentIds.value.forEach(value => emit('uploadRemoved', value));
		emit('done');
	};

	const cancel = () => {
		// On cancel, remove newly added attachments
		newAttachments.value.forEach(file => emit('uploadRemoved', file.id.toString()));
		emit('done');
	};

	/**
	 * Prevent dropping a file in the browser window (not in the dropping zone) from opening the file in a current tab.
	 * @param event
	 */
	const windowDragAndDropPrevent = (event: DragEvent) => {
		event.preventDefault();
	};

	onMounted(() => {
		window.addEventListener('dragover', windowDragAndDropPrevent);
		window.addEventListener('drop', windowDragAndDropPrevent);
	});

	onUnmounted(() => {
		window.removeEventListener('dragover', windowDragAndDropPrevent);
		window.removeEventListener('drop', windowDragAndDropPrevent);
	});

	const canDialogBeClosedWithoutLosingUploads = () => {
		if (state.size === 0) {
			return true;
		}
		// eslint-disable-next-line no-alert
		return confirm('Weet je zeker dat de geselecteerde bestanden niet wilt toevoegen?');
	};

	const uploadingInProgress = computed(() => newAttachments.value.some(item => item.tag === 'uploading'));
</script>

<style scoped>
	.dragndrop {
		outline: dashed 2px grey;
		padding: 20px;
		min-height: 80px;
		text-align: center;
		display: grid;
		align-content: center;
		justify-content: center;

		&.drag {
			background-color: var(--color-primary-opague);

			& > * {
				/* Prevent child elements from interfering with the drag event handling */
				pointer-events: none;
			}
		}

		&.invalid {
			background-color: var(--color-error);
		}

		&:not(.invalid) {
			.warning {
				display: none;
			}
		}

		.target {
			display: inline-block;
			color: var(--color-primary);
			cursor: pointer;

			&:deep(.attachment-icon) {
				transform: rotate(90deg);
				display: inline-block;
				--icon-size: 20px;
				pointer-events: none;
			}

			input {
				position: absolute;
				top: 0;
				right: 0;
				margin: 0;
				opacity: 0;
			}
		}
	}

	ul {
		list-style: none;
		padding: 20px 0 0;
		margin: 0;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		gap: 10px;
		max-width: 100%;
	}

	li {
		flex: 1 1 28em;
		max-width: 100%;
		overflow: hidden;
	}

	@media screen and (max-width: 767px) {
		ul {
			flex-direction: column;
		}

		li {
			flex: 0 0 auto;
		}
	}

	.dialog-buttons {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		margin-top: 20px;
		column-gap: 20px;
	}
</style>
